"use client";

import { clsx } from "clsx";
import React from "react";
import type { ColumnCount } from "./PricingTableV3Shared";
import { contentGridWithLabels } from "./PricingTableV3Shared";

export interface PricingTableColumn<TColumnId extends string = string> {
  id: TColumnId;
  /** Desktop header content (e.g., tier name with link/hover effects) */
  header: React.ReactNode;
  /** Label for mobile selector */
  mobileLabel: string;
}

export interface PricingTableSection {
  /** Optional section label (e.g., "Models", "Features") */
  label?: string;
  /** Rows within this section */
  rows: Array<{
    /** Left column: feature/row label */
    labelCell: React.ReactNode;
    /** Right columns: values for each column (indexed by column order) */
    valueCells: React.ReactNode[];
  }>;
}

export type MobileSelectorRenderFn<TColumnId extends string = string> = (props: {
  columns: PricingTableColumn<TColumnId>[];
  selectedColumnId?: TColumnId;
  onSelectionChange: (columnId: TColumnId) => void;
}) => React.ReactNode;

/** Base props shared across all pricing table components */
export interface PricingTableBaseProps<TColumnId extends string = string> {
  columns: PricingTableColumn<TColumnId>[];
  /** Selected column ID for mobile view */
  mobileSelectedColumnId?: TColumnId;
}

/** Props for components that support mobile selection */
export interface PricingTableMobileProps<TColumnId extends string = string> {
  /** Callback when mobile column selection changes */
  onMobileColumnChange?: (columnId: TColumnId) => void;
  /** Custom mobile selector component (receives columns and selection state) */
  renderMobileSelector?: MobileSelectorRenderFn<TColumnId>;
}

export interface PricingTableHeaderProps<TColumnId extends string = string>
  extends PricingTableBaseProps<TColumnId>, PricingTableMobileProps<TColumnId> {}

export interface PricingTableBodyProps<TColumnId extends string = string> extends PricingTableBaseProps<TColumnId> {
  sections: PricingTableSection[];
  /** Optional custom grid class to override the default contentGridWithLabels */
  gridClassName?: string;
  /** Optional custom class for the label column (default: tw-pl-10 tw-pr-4) */
  labelColumnClassName?: string;
  /** Optional custom class for the value columns (default: tw-px-3) */
  valueColumnClassName?: string;
}

export interface PricingComparisonTableProps<TColumnId extends string = string>
  extends PricingTableBaseProps<TColumnId>, PricingTableMobileProps<TColumnId> {
  sections: PricingTableSection[];
  className?: string;
}

/**
 * Header row for pricing comparison tables.
 * Renders column headers with optional mobile selector.
 *
 * @example
 * ```tsx
 * <PricingTableHeader
 *   columns={[
 *     { id: 'free', header: <TierHeader />, mobileLabel: 'Free' },
 *     { id: 'pro', header: <TierHeader />, mobileLabel: 'Pro' },
 *   ]}
 *   renderMobileSelector={({ columns, onSelectionChange }) => (
 *     <MobileSelector columns={columns} onChange={onSelectionChange} />
 *   )}
 * />
 * ```
 */
export function PricingTableHeader<TColumnId extends string = string>({
  columns,
  mobileSelectedColumnId,
  renderMobileSelector,
  onMobileColumnChange,
}: PricingTableHeaderProps<TColumnId>) {
  const columnCount = columns.length as ColumnCount;

  return (
    <div className={clsx("tw-border-b tw-border-black/5", contentGridWithLabels({ columnCount }))}>
      {/* Empty first cell - different padding for desktop/mobile */}
      <div className='tw-pt-10 tw-pb-6 tw-pl-10 tw-pr-4 max-md:tw-py-4 max-md:tw-px-4' />

      {/* Column headers - hidden on mobile */}
      {columns.map(column => (
        <div key={column.id} className='tw-text-right max-md:tw-hidden tw-relative tw-z-20'>
          {column.header}
        </div>
      ))}

      {/* Mobile Selector */}
      {renderMobileSelector && onMobileColumnChange && (
        <div className='md:tw-hidden'>
          {renderMobileSelector({
            columns,
            selectedColumnId: mobileSelectedColumnId,
            onSelectionChange: onMobileColumnChange,
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Body content for pricing comparison tables.
 * Renders sections with rows of labels and values.
 *
 * @example
 * ```tsx
 * <PricingTableBody
 *   columns={columns}
 *   sections={[
 *     {
 *       label: 'Features',
 *       rows: [
 *         { labelCell: 'API Access', valueCells: [<Check />, <Check />] },
 *       ]
 *     }
 *   ]}
 *   mobileSelectedColumnId="pro"
 * />
 * ```
 */
export function PricingTableBody<TColumnId extends string = string>({
  columns,
  sections,
  mobileSelectedColumnId,
  gridClassName,
  labelColumnClassName = "tw-pl-10 tw-pr-4",
  valueColumnClassName = "tw-px-3",
}: PricingTableBodyProps<TColumnId>) {
  const columnCount = columns.length as ColumnCount;
  const selectedColumnIndex = mobileSelectedColumnId ? columns.findIndex(col => col.id === mobileSelectedColumnId) : 0;
  const gridClass = gridClassName ?? contentGridWithLabels({ columnCount });

  return (
    <>
      {sections.map((section, sectionIndex) => {
        const hasLabel = !!section.label;
        const isLastSection = sectionIndex === sections.length - 1;

        return (
          <React.Fragment key={sectionIndex}>
            {/* Section Label Row */}
            {hasLabel && (
              <div
                className={clsx("tw-py-2 tw-border-b tw-relative tw-z-20 tw-border-black/5", labelColumnClassName)}
                style={{ background: "#F9F8F6" }}
              >
                <span className='tw-text-gray-600 tw-font-medium tw-type-2xs'>{section.label}</span>
              </div>
            )}

            {/* Section Rows */}
            <div className={clsx(!isLastSection && "tw-border-b tw-border-black/5", gridClass)}>
              {/* Feature Labels Column */}
              <div className={clsx("tw-align-top", labelColumnClassName)}>
                {section.rows.map((row, rowIndex) => (
                  <div key={rowIndex}>{row.labelCell}</div>
                ))}
              </div>

              {/* Value Columns */}
              {columns.map((column, columnIndex) => {
                const isMobileSelected = columnIndex === selectedColumnIndex;
                return (
                  <div
                    key={column.id}
                    className={clsx("tw-align-top", valueColumnClassName, !isMobileSelected && "max-md:tw-hidden")}
                  >
                    {section.rows.map((row, rowIndex) => (
                      <div key={rowIndex}>{row.valueCells[columnIndex]}</div>
                    ))}
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
}

/**
 * Complete pricing comparison table component.
 * Composes header and body with column dividers and responsive behavior.
 *
 * Features:
 * - Responsive grid layout with configurable columns
 * - Desktop: shows all columns side-by-side
 * - Mobile: shows one column at a time with selector
 * - Section grouping with optional labels
 * - Customizable column headers and mobile selector
 *
 * For more granular control, use `PricingTableHeader` and `PricingTableBody` separately.
 *
 * @example
 * ```tsx
 * <PricingComparisonTable
 *   columns={[
 *     { id: 'free', header: <TierHeader name="Free" />, mobileLabel: 'Free' },
 *     { id: 'pro', header: <TierHeader name="Pro" />, mobileLabel: 'Pro' },
 *   ]}
 *   sections={[
 *     {
 *       label: 'Features',
 *       rows: [
 *         { labelCell: 'API Access', valueCells: [<Check />, <Check />] },
 *         { labelCell: 'Support', valueCells: ['Email', '24/7'] },
 *       ]
 *     }
 *   ]}
 * />
 * ```
 */
export function PricingComparisonTable<TColumnId extends string = string>({
  columns,
  sections,
  mobileSelectedColumnId,
  onMobileColumnChange,
  renderMobileSelector,
  className,
}: PricingComparisonTableProps<TColumnId>) {
  const columnCount = columns.length as ColumnCount;
  const selectedColumnIndex = mobileSelectedColumnId ? columns.findIndex(col => col.id === mobileSelectedColumnId) : 0;

  return (
    <div className={clsx("tw-relative tw-min-h-[500px]", className)}>
      {/* Vertical Column Dividers */}
      <div
        className='tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-pointer-events-none tw-z-10'
        aria-hidden='true'
      >
        <div className={clsx("tw-h-full", contentGridWithLabels({ columnCount }))}>
          {/* Empty first column (for feature labels) */}
          <div />
          {/* Column dividers - on mobile only show selected column */}
          {columns.map((column, index) => {
            const isMobileSelected = index === selectedColumnIndex;
            return (
              <div
                key={column.id}
                className={clsx("tw-border-l tw-border-black/5", !isMobileSelected && "max-md:tw-hidden")}
              />
            );
          })}
        </div>
      </div>

      {/* Header */}
      <PricingTableHeader
        columns={columns}
        mobileSelectedColumnId={mobileSelectedColumnId}
        renderMobileSelector={renderMobileSelector}
        onMobileColumnChange={onMobileColumnChange}
      />

      {/* Body */}
      <PricingTableBody columns={columns} sections={sections} mobileSelectedColumnId={mobileSelectedColumnId} />
    </div>
  );
}
